import React, { useMemo, useState, useCallback, useEffect } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer, Cell,
} from "recharts";

function formatDuration(seconds) {
  if (seconds < 60)    return `~${Math.round(seconds)}s`;
  if (seconds < 3600)  return `~${Math.round(seconds / 60)}min`;
  if (seconds < 86400) return `~${(seconds / 3600).toFixed(1)}h`;
  return `~${(seconds / 86400).toFixed(1)}d`;
}

// Chooses one time unit for the entire axis based on the visible span,
// so all ticks use the same unit and Math.round keeps them as whole numbers.
function makeTickFormatter(axisSpan) {
  if (axisSpan < 120)    return (v) => `${Math.round(v)}s`;
  if (axisSpan < 7200)   return (v) => `${Math.round(v / 60)}min`;
  if (axisSpan < 172800) return (v) => `${Math.round(v / 3600)}h`;
  return                        (v) => `${Math.round(v / 86400)}d`;
}

// Generates ~5 explicit tick positions that are round multiples in the dominant
// time unit — avoids Recharts auto-ticks which land on raw-second values.
function computeAxisTicks(xLeft, xRight, targetCount = 5) {
  const span = xRight - xLeft;
  if (span <= 0) return [Math.round(xLeft)];
  let unit;
  if (span < 120)        unit = 1;
  else if (span < 7200)  unit = 60;
  else if (span < 172800) unit = 3600;
  else                   unit = 86400;
  const rawStep = (span / unit) / targetCount;
  const niceSteps = [1, 2, 5, 10, 15, 20, 30, 60, 120, 240, 360];
  const stepInUnit = niceSteps.find((s) => s >= rawStep) ?? niceSteps[niceSteps.length - 1];
  const step = stepInUnit * unit;
  const firstTick = Math.ceil(xLeft / step) * step;
  const ticks = [];
  for (let t = firstTick; t <= xRight + step * 0.01; t += step) {
    if (t <= xRight) ticks.push(Math.round(t));
  }
  return ticks.length > 0 ? ticks : [Math.round(xLeft)];
}

function computeHistogramBins(observations, numBins = 10) {
  if (!observations || observations.length === 0) return [];
  const durations = observations.map((o) => o.duration);
  const min = Math.min(...durations);
  const max = Math.max(...durations);
  if (min === max) {
    return [{
      midpoint: min,
      count: observations.length,
      object_ids: observations.map((o) => o.object_id),
      displayLabel: formatDuration(min),
    }];
  }
  const binWidth = (max - min) / numBins;
  const bins = Array.from({ length: numBins }, (_, i) => ({
    midpoint: min + (i + 0.5) * binWidth,
    displayLabel: formatDuration(min + (i + 0.5) * binWidth),
    count: 0,
    object_ids: [],
  }));
  for (const obs of observations) {
    const idx = Math.min(Math.floor((obs.duration - min) / binWidth), numBins - 1);
    bins[idx].count++;
    bins[idx].object_ids.push(obs.object_id);
  }
  return bins;
}

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const bin = payload[0].payload;
  return (
    <div className="bg-gray-800 text-white text-xs rounded px-2 py-1.5 shadow-lg">
      <div>{bin.displayLabel}</div>
      <div>{bin.count} object{bin.count !== 1 ? "s" : ""}</div>
    </div>
  );
};

// ── Temporal mode body — hooks live here so they aren't called conditionally ──

const TemporalPanelBody = ({ pairData, onFilterByObjectIds, activeObjectFilter, lifecycleEndingIds }) => {
  const activeObservations = useMemo(() => {
    const all = pairData.object_observations ?? [];
    if (!activeObjectFilter || activeObjectFilter.size === 0) return all;
    return all.filter((o) => activeObjectFilter.has(o.object_id));
  }, [pairData.object_observations, activeObjectFilter]);

  const bins = useMemo(
    () => computeHistogramBins(activeObservations),
    [activeObservations],
  );

  const allObjectIds = useMemo(
    () => [...new Set(activeObservations.map((o) => o.object_id))].sort(),
    [activeObservations],
  );

  const [selectedBins, setSelectedBins] = useState(() => new Set());
  const [selectedObjectIds, setSelectedObjectIds] = useState(() => new Set());

  useEffect(() => {
    setSelectedBins(new Set());
    setSelectedObjectIds(new Set());
  }, [activeObjectFilter]);

  const toggleBin = useCallback((idx) => {
    setSelectedBins((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  }, []);

  const filteredObjectIds = useMemo(() => {
    if (selectedBins.size === 0) return [];
    const ids = new Set();
    selectedBins.forEach((i) => bins[i]?.object_ids.forEach((id) => ids.add(id)));
    return [...ids];
  }, [selectedBins, bins]);

  const refX = pairData.expected_center;

  const axisRange = useMemo(() => {
    if (bins.length < 2) return 0;
    return bins[bins.length - 1].midpoint - bins[0].midpoint;
  }, [bins]);

  const halfBinWidth = useMemo(() => {
    if (bins.length < 2) return Math.max((bins[0]?.midpoint ?? 1) * 0.1, 1);
    return axisRange / (bins.length - 1) / 2;
  }, [bins, axisRange]);

  const domainMin = bins.length > 0 ? bins[0].midpoint - halfBinWidth : 0;
  const domainMax = bins.length > 0 ? bins[bins.length - 1].midpoint + halfBinWidth : 1;
  const dataSpan = domainMax - domainMin;

  const effectiveSpan = bins.length < 2 ? (bins[0]?.midpoint ?? 1) : dataSpan;
  const showRef = refX > 0 && refX >= domainMin - effectiveSpan && refX <= domainMax + effectiveSpan;

  const xLeft  = Math.max(0, showRef && refX < domainMin ? refX - halfBinWidth : domainMin);
  const xRight = showRef && refX > domainMax ? refX + halfBinWidth : domainMax;

  const barColor = "#60a5fa"; // blue-400, neutral

  return (
    <>
      {/* Stats row */}
      <div className="flex gap-3 px-3 pt-2 pb-1 text-xs text-gray-500 flex-shrink-0">
        <span>
          <span className="font-medium text-gray-700">{allObjectIds.length}</span> objects
        </span>
        {refX > 0 && (
          <span>
            Reference Time: <span className="font-medium text-gray-700">{formatDuration(refX)}</span>
          </span>
        )}
        <span>
          Is-Time: <span className="font-medium text-gray-700">{formatDuration(pairData.mean_observed)}</span>
        </span>
      </div>

      {/* Histogram */}
      <div className="px-1 pb-1 flex-shrink-0" style={{ height: 140 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={bins} margin={{ top: 6, right: 14, left: -18, bottom: 0 }} barCategoryGap="20%">
            <XAxis
              type="number"
              dataKey="midpoint"
              ticks={computeAxisTicks(xLeft, xRight)}
              tickFormatter={makeTickFormatter(xRight - xLeft)}
              tick={{ fontSize: 8, fill: "#9ca3af" }}
              axisLine={{ stroke: "#e5e7eb" }}
              tickLine={{ stroke: "#e5e7eb" }}
              domain={[xLeft, xRight]}
            />
            <YAxis
              tick={{ fontSize: 8, fill: "#9ca3af" }}
              axisLine={{ stroke: "#e5e7eb" }}
              tickLine={{ stroke: "#e5e7eb" }}
              allowDecimals={false}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f1f5f9" }} />
            <Bar dataKey="count" radius={[2, 2, 0, 0]} maxBarSize={80} onClick={(_, idx) => toggleBin(idx)} style={{ cursor: "pointer" }}>
              {bins.map((_, idx) => (
                <Cell key={idx} fill={selectedBins.has(idx) ? "#2563eb" : barColor} />
              ))}
            </Bar>
            {showRef && (
              <ReferenceLine
                x={refX}
                ifOverflow="hidden"
                stroke="#f87171"
                strokeWidth={1.5}
                strokeDasharray="4 2"
                label={{ value: "Ref", position: "top", fontSize: 8, fill: "#f87171" }}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Filter graph button — appears when bins are selected */}
      {selectedBins.size > 0 && (
        <div className="px-3 pt-1 pb-2 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={() => onFilterByObjectIds?.(filteredObjectIds)}
            className="w-full py-1.5 text-xs font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Filter graph · {filteredObjectIds.length} object{filteredObjectIds.length !== 1 ? "s" : ""}
          </button>
        </div>
      )}

      {/* Object-ID list — lifecycle-ending IDs marked with amber dot; click to local-select */}
      <div className="border-t border-gray-100 flex-1 overflow-y-auto">
        <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wide flex-shrink-0">
          Object IDs ({allObjectIds.length})
        </div>
        <ul className="px-3 pb-2 space-y-0.5">
          {allObjectIds.map((id) => {
            const isEnding = lifecycleEndingIds?.has(id);
            const isSelected = selectedObjectIds.has(id);
            return (
              <li key={id} className="text-xs font-mono truncate flex items-center gap-1.5">
                {isEnding && (
                  <span
                    title="This object's last recorded activity was this edge's target node"
                    className="flex-shrink-0 w-2 h-2 rounded-full bg-amber-400 cursor-help"
                  />
                )}
                <span
                  onClick={() => setSelectedObjectIds((prev) => {
                    const next = new Set(prev);
                    next.has(id) ? next.delete(id) : next.add(id);
                    return next;
                  })}
                  className={`truncate cursor-pointer hover:underline ${
                    isSelected ? "text-blue-700 font-semibold" : isEnding ? "text-amber-700" : "text-gray-600"
                  }`}
                >
                  {id}
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Filter by selected IDs — appears when any ID is selected */}
      {selectedObjectIds.size > 0 && (
        <div className="px-3 pt-1 pb-2 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={() => onFilterByObjectIds?.([...selectedObjectIds])}
            className="w-full py-1.5 text-xs font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Filter graph · {selectedObjectIds.size} object{selectedObjectIds.size !== 1 ? "s" : ""}
          </button>
        </div>
      )}
    </>
  );
};

// ── Sequential mode body — all traversing objects with lifecycle-ending marking ─

const SequentialPanelBody = ({ traversalObjectIds, lifecycleEndingIds, onFilterByObjectIds }) => {
  const allIds = traversalObjectIds ? [...traversalObjectIds].sort() : [];
  const [selectedObjectIds, setSelectedObjectIds] = useState(() => new Set());

  if (allIds.length === 0) {
    return (
      <div className="px-3 py-4 text-xs text-gray-500 text-center">
        No objects traverse this transition
      </div>
    );
  }

  return (
    <>
      {/* Stats row */}
      <div className="flex gap-3 px-3 pt-2 pb-1 text-xs text-gray-500 flex-shrink-0">
        <span>
          <span className="font-medium text-gray-700">{allIds.length}</span> objects
        </span>
      </div>

      {/* Object-ID list — lifecycle-ending IDs marked with amber dot; click to local-select */}
      <div className="border-t border-gray-100 flex-1 overflow-y-auto">
        <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wide flex-shrink-0">
          Object IDs ({allIds.length})
        </div>
        <ul className="px-3 pb-2 space-y-0.5">
          {allIds.map((id) => {
            const isEnding = lifecycleEndingIds?.has(id);
            const isSelected = selectedObjectIds.has(id);
            return (
              <li key={id} className="text-xs font-mono truncate flex items-center gap-1.5">
                {isEnding && (
                  <span
                    title="This object's last recorded activity was this edge's target node"
                    className="flex-shrink-0 w-2 h-2 rounded-full bg-amber-400 cursor-help"
                  />
                )}
                <span
                  onClick={() => setSelectedObjectIds((prev) => {
                    const next = new Set(prev);
                    next.has(id) ? next.delete(id) : next.add(id);
                    return next;
                  })}
                  className={`truncate cursor-pointer hover:underline ${
                    isSelected ? "text-blue-700 font-semibold" : isEnding ? "text-amber-700" : "text-gray-600"
                  }`}
                >
                  {id}
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Filter button — appears only when IDs are selected */}
      {selectedObjectIds.size > 0 && (
        <div className="px-3 pt-1 pb-2 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={() => onFilterByObjectIds?.([...selectedObjectIds])}
            className="w-full py-1.5 text-xs font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Filter graph · {selectedObjectIds.size} object{selectedObjectIds.size !== 1 ? "s" : ""}
          </button>
        </div>
      )}
    </>
  );
};

// ── Main panel — decides sequential vs temporal layout ────────────────────────

const EdgeDetailPanel = ({ edgeKey, pairData, onClose, panelStyle, onFilterByObjectIds, activeObjectFilter, lifecycleEndingIds, traversalObjectIds }) => {
  const [source, target] = edgeKey.split("→");

  const panelStyle_ = {
    position: "fixed",
    ...panelStyle,
    width: 300,
    maxHeight: "calc(100vh - 16px)",
    zIndex: 10,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  };

  const header = (
    <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 flex-shrink-0">
      <span className="text-xs font-semibold text-gray-700 truncate">
        {source} <span className="text-gray-400">→</span> {target}
      </span>
      <button
        onClick={onClose}
        className="ml-2 text-gray-400 hover:text-gray-600 text-sm leading-none flex-shrink-0"
        aria-label="Close"
      >
        ×
      </button>
    </div>
  );

  // Sequential mode: no temporal data — show all traversing objects with lifecycle-ending marking
  if (!pairData) {
    return (
      <div style={panelStyle_} className="bg-white border border-gray-200 rounded-xl shadow-lg">
        {header}
        <SequentialPanelBody
          traversalObjectIds={traversalObjectIds}
          lifecycleEndingIds={lifecycleEndingIds}
          onFilterByObjectIds={onFilterByObjectIds}
        />
      </div>
    );
  }

  // Temporal mode: full histogram + marked object list
  return (
    <div style={panelStyle_} className="bg-white border border-gray-200 rounded-xl shadow-lg">
      {header}
      <TemporalPanelBody
        pairData={pairData}
        onFilterByObjectIds={onFilterByObjectIds}
        activeObjectFilter={activeObjectFilter}
        lifecycleEndingIds={lifecycleEndingIds}
      />
    </div>
  );
};

export default EdgeDetailPanel;
